import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { EventsState } from "../../../store/types";
import { CallToAction, CallToActionType, LanguagesAbbr, ModuleMap, PageLinkType, PageModule } from "../../../types/working-model";
import { updateTranslateKey } from "../../../utils/utils";
import Icon, { COLORS, ICONS } from "../icon";
import SelectInput from "../select/select";
import TextInput from "../text-input/text";
import './call-to-action.scss';

//if more page links are needed add them here and to the PageLinkType in working-model.ts
const pageOptions = [
	{
		label: "Registration",
		value: PageLinkType.registration
	},
	{
		label: "Landing Page",
		value: PageLinkType.landing
	},
	{
		label: "Terms & Conditions",
		value: PageLinkType.termsAndConditions
	},
	{
		label: "Privacy Policy",
		value: PageLinkType.privacyPolicy
	},
	{
		label: "Home Page",
		value: PageLinkType.homepage
	},
	{
		label: "Agenda Page",
		value: PageLinkType.agenda
	}
];

const defaultTypeOptions: { label: string, value: CallToActionType }[] = [
	{
		label: "Section",
		value: CallToActionType.section
	},
	{
		label: "Page",
		value: CallToActionType.page
	},
	{
		label: "URL",
		value: CallToActionType.url
	}
];

interface CallToActionProps {
	workingCTA?: CallToAction;
	onWorkingCTAChange: (workingCallToAction: CallToAction) => void;
	useFocusClick?: boolean;
	language: LanguagesAbbr;
	baseLanguage: LanguagesAbbr;
	ctaTypeOptions?: CallToActionType[];
}

const initRegistrationCTA: CallToAction = {
	type: CallToActionType.page,
	button_name: {
		base: "Register now",
		changed: ""
	},
	content: { page: PageLinkType.registration },
};

const CallToActionForm: React.FC<CallToActionProps> = ({
	workingCTA,
	onWorkingCTAChange,
	useFocusClick,
	language,
	baseLanguage,
	ctaTypeOptions
}) => {
	if (!workingCTA) {
		workingCTA = initRegistrationCTA;
	}

	const {
		CreateEventReducer: {
			workingEvent
		},
	} = useSelector((event: EventsState) => event);

	function handleTypeChange(type: CallToActionType) {
		if (workingCTA) {
			onWorkingCTAChange({
				...workingCTA,
				type
			});
		}
	}
	function handleNameChange({ target }: React.ChangeEvent<HTMLInputElement>) {
		if (workingCTA) {
			onWorkingCTAChange({
				...workingCTA,
				button_name: updateTranslateKey({
					translateString: workingCTA?.button_name,
					input: target.value,
					language,
					baseLanguage
				})
			});
		}
	}
	function handleSectionDest(value: number) {
		if (workingCTA) {
			// intentionally removing section so we only use section_module_id in the future
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			const { content: { section, ...rest } } = workingCTA;
			onWorkingCTAChange({
				...workingCTA,
				content: {
					...rest,
					section_module_id: value,
				}
			});
		}
	}
	function handlePageDest(value: PageLinkType) {
		if (workingCTA) {
			onWorkingCTAChange({
				...workingCTA,
				content: { page: value }
			});
		}
	}
	function handleLinkDest(e: React.ChangeEvent<HTMLInputElement>) {
		if (workingCTA) {
			onWorkingCTAChange({
				...workingCTA,
				content: { url: e.target.value }
			});
		}
	}

	//module options
	const sectionOptions = useMemo(() => {
		if (!workingEvent?.homepage?.modules) return [];
		const opts = workingEvent?.homepage?.modules
			.filter(module => module.is_on)
			.map(module => {
				if (!module?.id) return null;
				return {
					value: module.id,
					label: (module?.content?.reference_title || ModuleMap[module.type] || '') as string,
				};
			})
			.filter(i => i) as { label: string; value: number | '' }[];

		// just adding an empty option at the beginning so the select input doesn't incorrectly display the first item
		// as "selected" when it isn't.
		opts?.unshift({ label: '', value: '' });
		return opts;
	}, [workingEvent]);

	const customPageOptions = workingEvent?.custom_pages
		? workingEvent.custom_pages.map(page => ({
			label: page.page_name.base,
			value: page.uuid
		}))
		: [];

	const extendedPageOptions = [...pageOptions, ...customPageOptions];

	const selectedSectionModule = useCallback(() => {
		if (workingCTA?.content?.section_module_id) {
			return workingCTA?.content?.section_module_id;
		}
		if (workingCTA?.content?.section || workingCTA?.content?.section === 0) { // this is for backwards compatability only
			// find the first module id, matching by type (section)
			const matchedModule = workingEvent?.homepage?.modules?.find((module) => {
				if (module.is_on && module.type === workingCTA?.content?.section) {
					return true;
				}
				return false;
			});
			if (matchedModule?.id) {
				return matchedModule.id;
			}
		}
		return '';
	}, [workingCTA.content.section, workingCTA.content?.section_module_id, workingEvent?.homepage?.modules]);

	return (
		<div className="call-to-action-settings">
			<div className="call-to-action-upper">
				<TextInput
					id="button-text"
					label="Button text"
					placeholder={"Enter button text"}
					defaultValue={workingCTA?.button_name?.[language] as string || workingCTA?.button_name?.base || ''}
					onChange={handleNameChange}
					useFocusClick={useFocusClick}
				/>
				<SelectInput
					mainIcon={<Icon name={ICONS.KEYBOARD_ARROW_DOWN} size={12} color={COLORS.WHITE} />}
					options={ctaTypeOptions ? defaultTypeOptions.filter(({ value }) => ctaTypeOptions.includes(value)) : defaultTypeOptions}
					selected={workingCTA.type}
					onChange={handleTypeChange}
					label={"Link to *"}
				/>

			</div>
			{workingCTA.type === CallToActionType.section && (
				<div className="call-to-action-lower call-to-action-section-options">
					<SelectInput
						id="select-section"
						mainIcon={<Icon name={ICONS.KEYBOARD_ARROW_DOWN} size={12} color={COLORS.WHITE} />}
						options={sectionOptions || []}
						// check if the section is main_event_banner which has enum value of 0
						selected={selectedSectionModule()}
						onChange={handleSectionDest}
						placeholder={"Select section"}
						label={"Section"}
						highlightSelectedItem
						hideEmptyOption
					/>
				</div>
			)}
			{workingCTA.type === CallToActionType.page && (
				<div className="call-to-action-lower">
					<SelectInput
						id="select-page"
						mainIcon={<Icon name={ICONS.KEYBOARD_ARROW_DOWN} size={12} color={COLORS.WHITE} />}
						options={extendedPageOptions}
						selected={workingCTA.content?.page || ""}
						onChange={handlePageDest}
						placeholder={"Select page"}
						label={"Page"}
					/>
				</div>
			)}

			{workingCTA.type === CallToActionType.url && (
				<div className="call-to-action-lower">
					<TextInput
						id="enter-url"
						label="URL"
						placeholder={"Enter URL"}
						onChange={handleLinkDest}
						defaultValue={workingCTA.content?.url}
						useFocusClick={useFocusClick}
					/>
				</div>
			)}
		</div>
	);
};

export default CallToActionForm;
